import { RuntimeHostSettings } from './environment.types';

const APP_SETTINGS_STORAGE_KEY = 'app-settings';
const APP_THEME_STORAGE_KEY = 'app-theme';
const APP_USER_STORAGE_KEY = 'app-user';
const APP_SELECTION_STORAGE_KEY = 'app-selection';

function toObject(value: unknown): Record<string, any> {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, any>
        : {};
}

function readStorageObject(storageKey: string): RuntimeHostSettings {
    if (typeof localStorage === 'undefined') {
        return {};
    }

    const raw = localStorage.getItem(storageKey);
    if (!raw) {
        return {};
    }

    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed as RuntimeHostSettings : {};
    } catch {
        localStorage.removeItem(storageKey);
        return {};
    }
}

function readAppSettingsFromStorage(): RuntimeHostSettings {
    return readStorageObject(APP_SETTINGS_STORAGE_KEY);
}

function readAppUserFromStorage(): RuntimeHostSettings {
    return readStorageObject(APP_USER_STORAGE_KEY);
}

function readAppThemeFromStorage(): RuntimeHostSettings {
    return readStorageObject(APP_THEME_STORAGE_KEY);
}

function readAppSelectionFromStorage(): RuntimeHostSettings {
    return readStorageObject(APP_SELECTION_STORAGE_KEY);
}

function readWindowRuntimeSettings(): RuntimeHostSettings {
    if (typeof window === 'undefined') {
        return {};
    }

    const legacySettings = (window as any).settings;
    const legacyObject = legacySettings && typeof legacySettings === 'object'
        ? legacySettings as RuntimeHostSettings
        : {};

    return {
        ...legacyObject,
        url: window.url ?? legacyObject.url,
        layout: window.layout ?? legacyObject.layout,
        support: window.support ?? legacyObject.support,
        app: window.app ?? legacyObject.app,
        organizations: window.organizations ?? legacyObject.organizations,
        user: window.user ?? legacyObject.user,
        access: window.access ?? legacyObject.access,
        roles: window.roles ?? legacyObject.roles,
        selection: window.selection ?? legacyObject.selection
    };
}

function writeWindowRuntimeSettings(settings: RuntimeHostSettings): void {
    if (typeof window === 'undefined') {
        return;
    }

    window.settingsConfig = settings.settings;
    window.themeConfig = settings.theme;
    window.userConfig = settings.userState;
    window.selectionConfig = settings.selectionState;
    window.url = settings.url;
    window.layout = settings.layout;
    window.support = settings.support;
    window.app = settings.app;
    window.organizations = settings.organizations;
    window.user = settings.user;
    window.access = settings.access;
    window.roles = settings.roles;
    window.selection = settings.selection;

    // Keep compatibility with legacy consumers still reading window.settings.
    (window as any).settings = settings;
}

export function readRuntimeSettings(): RuntimeHostSettings {
    const hostSettings: RuntimeHostSettings = readWindowRuntimeSettings();
    const storedSettings = readAppSettingsFromStorage();
    const storedTheme = readAppThemeFromStorage();
    const storedUser = readAppUserFromStorage();
    const storedSelection = readAppSelectionFromStorage();
    const storedUserAny = storedUser as any;
    const storedSelectionAny = storedSelection as any;

    const settingsNamespace = {
        ...toObject(hostSettings.settings),
        ...toObject(storedSettings),
        ...toObject(storedSettings.settings),
        url: {
            ...toObject(hostSettings.settings?.url),
            ...toObject(hostSettings.url),
            ...toObject(storedSettings.url),
            ...toObject(storedSettings.settings?.url)
        },
        app: {
            ...toObject(hostSettings.settings?.app),
            ...toObject(hostSettings.app),
            ...toObject(storedSettings.app),
            ...toObject(storedSettings.settings?.app)
        },
        support: {
            ...toObject(hostSettings.settings?.support),
            ...toObject(hostSettings.support),
            ...toObject(storedSettings.support),
            ...toObject(storedSettings.settings?.support)
        }
    };

    const themeNamespace = {
        ...toObject(hostSettings.theme),
        ...toObject(hostSettings.layout),
        ...toObject(storedTheme),
        ...toObject(storedTheme.theme),
        ...toObject(storedTheme.layout)
    };

    const userNamespace = {
        ...toObject(hostSettings.userState),
        ...toObject(storedUser),
        ...toObject(storedUser.userState),
        profile: {
            ...toObject(hostSettings.userState?.profile),
            ...toObject(hostSettings.user),
            ...toObject(storedUser.user),
            ...toObject(storedUserAny.profile),
            ...toObject(storedUser.userState?.profile)
        },
        roles: {
            ...toObject(hostSettings.userState?.roles),
            ...toObject(hostSettings.roles),
            ...toObject(storedUser.roles),
            ...toObject(storedUser.userState?.roles)
        },
        access: storedUser.userState?.access ?? storedUser.access ?? hostSettings.userState?.access ?? hostSettings.access ?? {},
        organizations: storedUser.userState?.organizations ?? storedUser.organizations ?? hostSettings.userState?.organizations ?? hostSettings.organizations
    };

    const selectionNamespace = {
        ...toObject(hostSettings.selectionState),
        ...toObject(storedSelection),
        ...toObject(storedSelection.selectionState),
        organization: storedSelection.selectionState?.organization ?? storedSelectionAny.organization ?? null,
        branch: storedSelection.selectionState?.branch ?? storedSelectionAny.branch ?? null,
        indexes: {
            ...toObject(hostSettings.selectionState?.indexes),
            ...toObject(hostSettings.selection),
            ...toObject(storedSelection.selection),
            ...toObject(storedSelectionAny.indexes),
            ...toObject(storedSelection.selectionState?.indexes)
        }
    };

    const mergedSettings: RuntimeHostSettings = {
        ...hostSettings,
        settings: settingsNamespace,
        theme: themeNamespace,
        userState: userNamespace,
        selectionState: selectionNamespace,
        url: settingsNamespace.url,
        app: settingsNamespace.app,
        support: settingsNamespace.support,
        layout: themeNamespace,
        user: userNamespace.profile,
        roles: userNamespace.roles,
        access: userNamespace.access,
        organizations: userNamespace.organizations,
        selection: {
            ...toObject(selectionNamespace.indexes),
            organizationData: selectionNamespace.organization,
            branchData: selectionNamespace.branch
        }
    };

    writeWindowRuntimeSettings(mergedSettings);

    return mergedSettings;
}
